import * as t from "io-ts";
import { Ord as stringOrd } from "fp-ts/lib/string";
import * as E from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import * as Ord from "fp-ts/lib/Ord";

export const assuredGuaranty = {
  _tag: `AssuredGuaranty`,
  id: 1,
  name: `Assured Guaranty`
} as const;

export type AssuredGuarantyTaggedC = t.TypeC<{
  _tag: t.LiteralC<`AssuredGuaranty`>
}>;
export const assuredGuarantyTaggedC: AssuredGuarantyTaggedC = t.type({
  _tag: t.literal(`AssuredGuaranty`)
});
export type AssuredGuarantyTagged = t.TypeOf<AssuredGuarantyTaggedC>;
export type AssuredGuaranty = AssuredGuarantyTagged & typeof assuredGuaranty;
export type AssuredGuarantyC = t.Type<AssuredGuaranty, AssuredGuarantyTagged>;
export const assuredGuarantyC: AssuredGuarantyC = pipe(assuredGuarantyTaggedC, c => new t.Type<AssuredGuaranty, AssuredGuarantyTagged>(
  `AssuredGuaranty`,
  (u: unknown): u is AssuredGuaranty => E.isRight(c.decode(u)),
  (u: unknown): E.Either<t.Errors, AssuredGuaranty> => pipe(c.decode(u), E.map(x => ({ ...x, ...assuredGuaranty }))),
  (x: AssuredGuaranty): AssuredGuarantyTagged => ({ ...x, _tag: `AssuredGuaranty`}),
)) satisfies t.Type<AssuredGuaranty, unknown>;


export const buildAmericaMutual = {
  _tag: `BuildAmericaMutual`,
  id: 2,
  name: `Build America Mutual`
} as const;

export type BuildAmericaMutualTaggedC = t.TypeC<{
  _tag: t.LiteralC<`BuildAmericaMutual`>
}>;
export const buildAmericaMutualTaggedC: BuildAmericaMutualTaggedC = t.type({
  _tag: t.literal(`BuildAmericaMutual`)
});
export type BuildAmericaMutualTagged = t.TypeOf<BuildAmericaMutualTaggedC>;
export type BuildAmericaMutual = BuildAmericaMutualTagged & typeof buildAmericaMutual;
export type BuildAmericaMutualC = t.Type<BuildAmericaMutual, BuildAmericaMutualTagged>;
export const buildAmericaMutualC: BuildAmericaMutualC = pipe(buildAmericaMutualTaggedC, c => new t.Type<BuildAmericaMutual, BuildAmericaMutualTagged>(
  `BuildAmericaMutual`,
  (u: unknown): u is BuildAmericaMutual => E.isRight(c.decode(u)),
  (u: unknown): E.Either<t.Errors, BuildAmericaMutual> => pipe(c.decode(u), E.map(x => ({ ...x, ...buildAmericaMutual }))),
  (x: BuildAmericaMutual): BuildAmericaMutualTagged => ({ ...x, _tag: `BuildAmericaMutual`}),
)) satisfies t.Type<BuildAmericaMutual, unknown>;


export const municipalAssuranceCorp = {
  _tag: `MunicipalAssuranceCorp`,
  id: 3,
  name: `Municipal Assurance Corp`
} as const;

export type MunicipalAssuranceCorpTaggedC = t.TypeC<{
  _tag: t.LiteralC<`MunicipalAssuranceCorp`>
}>;
export const municipalAssuranceCorpTaggedC: MunicipalAssuranceCorpTaggedC = t.type({
  _tag: t.literal(`MunicipalAssuranceCorp`)
});
export type MunicipalAssuranceCorpTagged = t.TypeOf<MunicipalAssuranceCorpTaggedC>;
export type MunicipalAssuranceCorp = MunicipalAssuranceCorpTagged & typeof municipalAssuranceCorp;
export type MunicipalAssuranceCorpC = t.Type<MunicipalAssuranceCorp, MunicipalAssuranceCorpTagged>;
export const municipalAssuranceCorpC: MunicipalAssuranceCorpC = pipe(municipalAssuranceCorpTaggedC, c => new t.Type<MunicipalAssuranceCorp, MunicipalAssuranceCorpTagged>(
  `MunicipalAssuranceCorp`,
  (u: unknown): u is MunicipalAssuranceCorp => E.isRight(c.decode(u)),
  (u: unknown): E.Either<t.Errors, MunicipalAssuranceCorp> => pipe(c.decode(u), E.map(x => ({ ...x, ...municipalAssuranceCorp }))),
  (x: MunicipalAssuranceCorp): MunicipalAssuranceCorpTagged => ({ ...x, _tag: `MunicipalAssuranceCorp`}),
)) satisfies t.Type<MunicipalAssuranceCorp, unknown>;


export const syncora = {
  _tag: `Syncora`,
  id: 4,
  name: `Syncora`
} as const;

export type SyncoraTaggedC = t.TypeC<{
  _tag: t.LiteralC<`Syncora`>
}>;
export const syncoraTaggedC: SyncoraTaggedC = t.type({
  _tag: t.literal(`Syncora`)
});
export type SyncoraTagged = t.TypeOf<SyncoraTaggedC>;
export type Syncora = SyncoraTagged & typeof syncora;
export type SyncoraC = t.Type<Syncora, SyncoraTagged>;
export const syncoraC: SyncoraC = pipe(syncoraTaggedC, c => new t.Type<Syncora, SyncoraTagged>(
  `Syncora`,
  (u: unknown): u is Syncora => E.isRight(c.decode(u)),
  (u: unknown): E.Either<t.Errors, Syncora> => pipe(c.decode(u), E.map(x => ({ ...x, ...syncora }))),
  (x: Syncora): SyncoraTagged => ({ ...x, _tag: `Syncora`}),
)) satisfies t.Type<Syncora, unknown>;


export const allRatingInsurerC = [assuredGuarantyC, buildAmericaMutualC, municipalAssuranceCorpC, syncoraC] as const;
export const allRatingInsurerNames = [`AssuredGuaranty`, `BuildAmericaMutual`, `MunicipalAssuranceCorp`, `Syncora`] as const;
export type RatingInsurerName = (typeof allRatingInsurerNames)[number];

export type RatingInsurerCU = t.UnionC<[AssuredGuarantyC, BuildAmericaMutualC, MunicipalAssuranceCorpC, SyncoraC]>;
export type RatingInsurerU = AssuredGuaranty | BuildAmericaMutual | MunicipalAssuranceCorp | Syncora;
export const RatingInsurerCU: RatingInsurerCU = t.union([assuredGuarantyC, buildAmericaMutualC, municipalAssuranceCorpC, syncoraC]) satisfies t.Type<RatingInsurerU, unknown>;

export const ratingInsurerOrd: Ord.Ord<RatingInsurerU> = pipe(stringOrd, Ord.contramap(x => x._tag));
export const allRatingInsurer = [assuredGuaranty, buildAmericaMutual, municipalAssuranceCorp, syncora] as const;
export type RatingInsurerMap<A> = { [K in RatingInsurerName]: A };


